"use client";
import Image from "next/image";

/* -----------------Components--------------- */
import { ActionGroup } from "@/components/ActionGroup";
import BackgroundSvg from "@/components/BackgroundSvg";
import { ComingSoon } from "@/components/ComingSoon";
import CompanyLogo from "@/components/CompanyLogo";
import {
	AdaptiveText,
	type AdaptiveTextProps,
	Title,
} from "@/components/Content";
import {
	ScaleIn,
	StaggerContainer,
	StaggerItem,
	StaggeredSlideInRight,
} from "@/components/animations/page-transitions";
import { Label } from "@/components/ui/label";

/* -----------------Helpers--------------- */
import { cn } from "@/lib/utils";

/* -----------------Types--------------- */
import type { ActionButtonProps } from "@/components/ActionButton";
import { Container } from "@/components/Container";
import { Sizes } from "@/components/Content/styles";
import type { ImageDetails } from "@/types";

type Props = {
	title: string;
	text: AdaptiveTextProps["portableText"];
	logo: ImageDetails;
	image: ImageDetails;
	actions: ActionButtonProps[];
	otherItemsTitle?: string;
	otherItemsLogos?: ImageDetails[];
	comingSoon?: boolean;
	id?: string;
	className?: string;
};

const ProductDisplay = ({
	title,
	text,
	logo,
	image,
	actions,
	otherItemsTitle = "Replaces",
	otherItemsLogos,
	comingSoon = false,
	id,
	className,
}: Props) => {
	return (
		<section id={id} className="relative pt-40 xl:pt-52">
			<BackgroundSvg position="bottom-right" />
			<Container
				className={cn("flex flex-col gap-x-6 gap-y-16 xl:flex-row", className)}
			>
				<div className="flex w-full max-w-2xl items-center justify-center xl:aspect-square xl:max-w-xl">
					<StaggerContainer>
						{logo.src && (
							<StaggerItem>
								<Image
									src={logo.src}
									alt={logo.alt}
									width={512}
									height={512}
									className="w-16 object-cover"
								/>
							</StaggerItem>
						)}

						<StaggerItem>
							<Title
								className="mt-4 max-w-4xl xl:max-w-none"
								size={Sizes.SecondaryHero}
							>
								{title}
							</Title>
						</StaggerItem>

						<StaggerItem>
							<AdaptiveText
								portableText={text}
								size={Sizes.SecondaryHero}
								className="max-w-4xl xl:max-w-none"
							/>
						</StaggerItem>

						{otherItemsLogos && (
							<StaggerItem>
								<div className="mt-10">
									{otherItemsTitle && (
										<Label className="font-semibold">{otherItemsTitle}</Label>
									)}

									<StaggerContainer
										staggerDelay={0.1}
										initialDelay={0.5}
										className="mt-2 flex items-center justify-start gap-x-4"
									>
										{otherItemsLogos.map((logo) => (
											<StaggeredSlideInRight key={logo.alt}>
												<CompanyLogo logo={logo} size={32} className="w-8" />
											</StaggeredSlideInRight>
										))}
									</StaggerContainer>
								</div>
							</StaggerItem>
						)}

						<StaggerItem>
							<ActionGroup actions={actions} className="mt-10" />
						</StaggerItem>
					</StaggerContainer>
				</div>

				<div className="flex w-full items-start justify-center xl:aspect-square xl:items-center">
					<ScaleIn>
						{comingSoon ? (
							<ComingSoon className="mx-auto w-full sm:w-[600px]" />
						) : (
							<Image
								src={image.src}
								alt={image.alt}
								width={1920}
								height={1080}
								className="w-full"
							/>
						)}
					</ScaleIn>
				</div>
			</Container>
		</section>
	);
};

export default ProductDisplay;
